import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { backendApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { canDestroySandbox, canManageSandbox, canChangeSiteChannel } from '../lib/permissions';
import { fmtDate, fmtDateTime } from '../lib/dates';
import { useModalEscape } from '../lib/useModalEscape';
import { recordingsUrl } from '../lib/posthog';
import { StatCard, Icon } from '../components/Icon';
import { ConversationModal } from './Accounts';
import {
  SITES_PATH, PAGE_SIZE, ACTIVITY_PAGE_SIZE, EMPTY_FILTERS, buildListQuery, isEndpointMissing, isDestroyable,
  activityPath, typeSplit, tileSplit, canExtendOrKeep,
  type ListFilters, type SiteRow, type SiteDetails, type SitesListResponse, type SitesSummary,
  type SiteState, type SortKey, type ActivityPage, type ActivityRow,
} from '../lib/sites';

const PLAN_OPTIONS: Array<[string, string]> = [
  ['free', 'Free'], ['standard', 'Standard'], ['team', 'Agency (team)'], ['agency', 'Agency (custom)'],
  ['internal', 'Internal'], ['pilot', 'Pilot'], ['pro', 'Pro'], ['pro_500', 'Pro 500'],
  ['pro_3000', 'Pro 3000'], ['pro_10000', 'Pro 10000'],
];

const STATE_BADGE: Record<SiteState, string> = {
  active: 'badge-green', deactivated: 'badge-amber', archived: 'badge-gray',
};

const CONNECTOR_LABELS: Record<string, string> = {
  google: 'Google', figma: 'Figma', figmaOfficial: 'Figma (official)', stitch: 'Stitch',
  woocommerce: 'WooCommerce', jotform: 'Jotform',
};

const short = (url?: string | null) => (url ? url.replace(/^https?:\/\//, '').replace(/\/$/, '') : '—');

/** "3d ago" style, for the Last seen column. */
function ago(s?: string | null): string {
  if (!s) return 'never';
  const t = new Date(s).getTime();
  if (Number.isNaN(t)) return '—';
  const secs = Math.max(0, (Date.now() - t) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60); if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60); if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24); if (days < 60) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export default function Sites() {
  const { can } = useAuth();
  const canChats = can('accounts', 'read');
  const canRecordings = can('sites', 'read');
  const canDestroy = canDestroySandbox(can);
  const canManage = canManageSandbox(can);
  const canChannel = canChangeSiteChannel(can);

  const [filters, setFilters] = useState<ListFilters>(EMPTY_FILTERS);
  // The search box updates instantly; the request follows after a pause.
  const [searchText, setSearchText] = useState('');
  const [data, setData] = useState<SitesListResponse | null>(null);
  const [error, setError] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [details, setDetails] = useState<string | null>(null);
  const [toDestroy, setToDestroy] = useState<SiteRow | null>(null);
  const seq = useRef(0);

  const load = useCallback(async (f: ListFilters) => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const d = await backendApi.get<SitesListResponse>(`${SITES_PATH}?${buildListQuery(f)}`);
      if (mine !== seq.current) return; // a newer request superseded this one
      setData(d); setError(''); setUnavailable(false);
    } catch (err: any) {
      if (mine !== seq.current) return;
      if (isEndpointMissing(err)) setUnavailable(true);
      else setError((err as ApiError).isForbidden ? 'You do not have access to sites.' : err.message);
    } finally { if (mine === seq.current) setLoading(false); }
  }, []);

  useEffect(() => { void load(filters); }, [filters, load]);

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.search === searchText ? f : { ...f, search: searchText, page: 0 }));
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const set = (patch: Partial<ListFilters>) => setFilters((f) => ({ ...f, ...patch, page: 0 }));

  async function destroy(row: SiteRow) {
    setError('');
    try {
      await backendApi.delete(`/trial/${row.sandbox!.trialId}`);
      setToDestroy(null);
      setNote(`Sandbox ${short(row.url)} destroyed.`);
      await load(filters);
    } catch (err: any) {
      setToDestroy(null);
      setError(err.body?.error || err.message);
    }
  }

  if (unavailable) {
    return (
      <div className="empty-state">
        <h2>Sites &amp; Sandboxes isn't available yet</h2>
        <p className="muted">The connected backend doesn't serve this section yet. It will appear once the backend is updated.</p>
      </div>
    );
  }

  const total = data?.total ?? 0;
  const offset = data?.offset ?? 0;
  const rows = data?.sites ?? [];
  const from = total === 0 ? 0 : offset + 1;
  const to = offset + rows.length;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <>
      <header className="page-head">
        <div><h1>Sites &amp; Sandboxes</h1><p className="muted">Every site and sandbox, and what each customer is running.</p></div>
        <button className="btn" onClick={() => void load(filters)} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
      </header>

      {data && <SummaryTiles s={data.summary} />}

      {error && <div className="alert alert-error">{error}</div>}
      {note && <div className="alert alert-ok">{note} <button className="alert-dismiss" onClick={() => setNote('')}>Dismiss</button></div>}

      <div className="card">
        <div className="filter-bar">
          <input type="search" aria-label="Search sites" placeholder="Search URL, sandbox link, account email or id…"
            value={searchText} onChange={(e) => setSearchText(e.target.value)} style={{ flex: '1 1 260px' }} />
          <select aria-label="Type" value={filters.type} onChange={(e) => set({ type: e.target.value as ListFilters['type'] })}>
            <option value="">All types</option><option value="site">Sites</option><option value="sandbox">Sandboxes</option>
          </select>
          <select aria-label="State" value={filters.state} onChange={(e) => set({ state: e.target.value as ListFilters['state'] })}>
            <option value="">All states</option><option value="active">Active</option>
            <option value="deactivated">Deactivated</option><option value="archived">Archived</option>
          </select>
          <select aria-label="Plan" value={filters.plan} onChange={(e) => set({ plan: e.target.value })}>
            <option value="">All plans</option>
            {PLAN_OPTIONS.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select aria-label="Sort" value={filters.sort} onChange={(e) => set({ sort: e.target.value as SortKey })}>
            <option value="newest">Newest first</option><option value="oldest">Oldest first</option>
            <option value="seen_recent">Seen most recently</option><option value="seen_stale">Seen longest ago</option>
          </select>
        </div>
      </div>

      <div className="card">
        {!data && loading ? <p className="muted">Loading…</p> : rows.length === 0 ? (
          <p className="muted">{loading ? 'Loading…' : 'No sites or sandboxes match.'}</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr>
                <th>Site</th><th>Owner</th><th>Plan</th><th>Created</th><th>Last seen</th><th>Plugin</th><th>Credits used</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <SiteRowView key={r.id} row={r} canDestroy={canDestroy}
                    recordingsHref={canRecordings ? recordingsUrl(r.account.email, { site: siteRecordingHost(r) }) : null}
                    onDetails={() => setDetails(r.id)} onDestroy={() => setToDestroy(r)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="filter-bar" style={{ justifyContent: 'space-between', marginTop: 12 }}>
          <span className="muted small" aria-live="polite">
            {total === 0 ? '0 results' : `Showing ${from}–${to} of ${total}`}
          </span>
          <div className="row-actions">
            <button className="btn btn-sm" disabled={loading || filters.page <= 0}
              onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}>Previous</button>
            <span className="muted small">Page {filters.page + 1} of {lastPage + 1}</span>
            <button className="btn btn-sm" disabled={loading || filters.page >= lastPage}
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}>Next</button>
          </div>
        </div>
      </div>

      {details && <DetailsModal id={details} canChats={canChats} canManage={canManage} canChannel={canChannel}
        onChanged={() => void load(filters)} onClose={() => setDetails(null)} />}
      {toDestroy && <ConfirmDestroy row={toDestroy} onCancel={() => setToDestroy(null)} onConfirm={() => destroy(toDestroy)} />}
    </>
  );
}

/**
 * Every tile shows the combined number, and under it how much is real sites
 * and how much is sandboxes, or "Sites only" / "Sandboxes only" when it all
 * belongs to one side. A backend that predates the split still gets the
 * numbers, without the second line.
 */
function SummaryTiles({ s }: { s: SitesSummary }) {
  const by = s.byType;
  const split = (k: keyof NonNullable<SitesSummary['byType']>['site']) => (by ? tileSplit(by.site[k], by.sandbox[k]) : undefined);
  return (
    <div className="stat-row" data-testid="sites-summary">
      <StatCard icon="globe" tint="purple" value={s.total} label="Sites & sandboxes" sub={typeSplit(s.sites, s.sandboxes)} />
      <StatCard icon="play" tint="green" value={s.active} label="Active" sub={split('active')} />
      <StatCard icon="alert" tint="amber" value={s.deactivated} label="Deactivated" sub={split('deactivated')} />
      <StatCard icon="alert" tint="gray" value={s.archived} label="Archived" sub={split('archived')} />
      <StatCard icon="calendar" tint="purple" value={s.createdLast7Days} label="Created (7 days)" sub={split('createdLast7Days')} />
      <StatCard icon="trending" tint="green" value={s.activeLast7Days} label="Seen (7 days)" sub={split('activeLast7Days')} />
      <StatCard icon="clock" tint="gray" value={s.noPluginConnection} label="No plugin connection" sub={split('noPluginConnection')} />
    </div>
  );
}

/** A small inline copy button that sits right after the text it copies. */
function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(value);
      else {
        // Clipboard API is unavailable on non-secure origins.
        const ta = document.createElement('textarea');
        ta.value = value; document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { /* the value is visible right next to the button */ }
  }
  return (
    <button type="button" className={'btn-icon btn-icon-inline' + (copied ? ' copied' : '')}
      title={copied ? 'Copied' : label} aria-label={label} onClick={() => void copy()}>
      <Icon name={copied ? 'check' : 'copy'} size={12} />
    </button>
  );
}

/** The host PostHog sees the visitor on: a sandbox's *.try link, else the site's own URL. */
const siteRecordingHost = (r: SiteRow) => (r.type === 'sandbox' && r.sandbox?.linkHost) || r.url;

function SiteRowView({ row: r, canDestroy, recordingsHref, onDetails, onDestroy }: {
  row: SiteRow; canDestroy: boolean; recordingsHref: string | null; onDetails: () => void; onDestroy: () => void;
}) {
  const isSandbox = r.type === 'sandbox';
  const primary = isSandbox && r.sandbox?.linkHost ? r.sandbox.linkHost : short(r.url);
  return (
    <tr>
      <td>
        <div className="inline-copy" style={{ fontWeight: 600 }}>
          {r.openUrl
            ? <a className="link" href={r.openUrl} target="_blank" rel="noreferrer noopener" title="Open in a new tab">{primary}</a>
            : <span>{primary}</span>}
          {primary !== '—' && <CopyButton value={primary} label="Copy name" />}
        </div>
        <div className="user-meta">
          <span className={'badge ' + (isSandbox ? 'badge-purple' : 'badge-gray')}>{isSandbox ? 'sandbox' : 'site'}</span>{' '}
          <span className={'badge ' + STATE_BADGE[r.state]}>{r.state}</span>
          {r.sandbox?.persisted && <> <span className="badge badge-teal badge-type">permanent</span></>}
        </div>
        <div className="inline-copy muted small mono">
          <span>{r.id}</span>
          <CopyButton value={r.id} label="Copy id" />
        </div>
      </td>
      <td>
        <div className="user-email">{r.account.email}</div>
        {r.account.name && <div className="muted small">{r.account.name}</div>}
      </td>
      <td><span className="badge badge-purple">{r.plan.name}</span></td>
      <td className="muted">{fmtDate(r.createdAt)}</td>
      <td className="muted" title={r.lastSeenAt ? fmtDateTime(r.lastSeenAt) : 'The plugin has never connected'}>{ago(r.lastSeenAt)}</td>
      <td>
        <div className="mono">{r.pluginVersion || '—'}</div>
        {r.releaseChannel && <div className="muted small">{r.releaseChannel} channel</div>}
      </td>
      <td className="mono">{r.creditsUsed.toLocaleString()}</td>
      <td>
        <div className="row-actions">
          <button className="btn-icon" title="View details" aria-label="View details" onClick={onDetails}><Icon name="eye" size={15} /></button>
          {recordingsHref && (
            <a className="btn-icon" title="Recordings (PostHog)" aria-label="Recordings (PostHog)"
              href={recordingsHref} target="_blank" rel="noopener noreferrer">
              <Icon name="play" size={15} />
            </a>
          )}
          {canDestroy && isDestroyable(r) && (
            <button className="btn-icon danger" title="Destroy sandbox" aria-label="Destroy sandbox" onClick={onDestroy}><Icon name="trash" size={15} /></button>
          )}
        </div>
      </td>
    </tr>
  );
}

function ConfirmDestroy({ row, onCancel, onConfirm }: { row: SiteRow; onCancel: () => void; onConfirm: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  useModalEscape(onCancel);
  return createPortal(
    <div className="modal-scrim" onClick={(e) => { e.stopPropagation(); onCancel(); }}>
      <div className="modal" role="alertdialog" aria-modal="true" aria-label="Confirm destroy sandbox" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><h2 className="card-title" style={{ margin: 0 }}>Destroy this sandbox?</h2></div>
        <p style={{ margin: '14px 0 10px' }}>
          <strong>{row.sandbox?.linkHost || short(row.url)}</strong><br />
          <span className="muted small">{row.account.email}</span>
        </p>
        <p className="muted small" style={{ margin: '0 0 16px' }}>
          This tears down the sandbox container and its WordPress content. It cannot be undone.
        </p>
        <div className="row-actions">
          <button className="btn btn-danger btn-sm" disabled={busy}
            onClick={async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } }}>
            {busy ? 'Destroying…' : 'Destroy sandbox'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>
        </div>
      </div>
    </div>, document.body);
}

type DetailsTab = 'site' | 'account' | 'sandbox' | 'connectors' | 'conversations' | 'credits';

function DetailsModal({ id, canChats, canManage, canChannel, onChanged, onClose }: {
  id: string; canChats: boolean; canManage: boolean; canChannel: boolean; onChanged: () => void; onClose: () => void;
}) {
  const [site, setSite] = useState<SiteDetails | null>(null);
  const [error, setError] = useState('');
  const [chat, setChat] = useState<string | null>(null);
  const [tab, setTab] = useState<DetailsTab>('site');
  const [reload, setReload] = useState(0);
  useModalEscape(onClose);

  useEffect(() => {
    let live = true;
    backendApi.get<{ site: SiteDetails }>(`${SITES_PATH}/${encodeURIComponent(id)}`)
      .then((d) => { if (live) setSite(d.site); })
      .catch((err: any) => { if (live) setError(err.status === 404 ? 'This site no longer exists.' : err.message); });
    return () => { live = false; };
  }, [id, reload]);

  const connectors = site ? [
    ...Object.entries(site.connectors.firstParty).filter(([, on]) => on).map(([k]) => CONNECTOR_LABELS[k] || k),
    ...site.connectors.composio.map((c) => `${c.toolkit}${c.scope === 'account' ? ' (account)' : ''}`),
  ] : [];
  const tabs: Array<[DetailsTab, string]> = [['site', 'Site'], ['account', 'Account'],
    ...(site?.sandbox ? [['sandbox', 'Sandbox'] as [DetailsTab, string]] : []), ['connectors', `Connectors (${connectors.length})`],
    ...(canChats ? [['conversations', `Conversations${site?.conversationCount != null ? ` (${site.conversationCount})` : ''}`] as [DetailsTab, string]] : []),
    ['credits', 'Credit usage']];

  return createPortal(
    <div className="modal-scrim" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="modal account-modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="account-modal-title">
            <h2 className="card-title" style={{ margin: 0 }}>{site ? (site.sandbox?.linkHost || short(site.url)) : 'Site details'}</h2>
            {site && <span className="muted small">{site.type} · {site.state}</span>}
          </div>
          <button className="btn-icon conv-modal-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="account-modal-body">
          {error ? <div className="alert alert-error">{error}</div> : !site ? <p className="muted small">Loading…</p> : (
            <>
              <div className="seg" role="tablist" aria-label="Details sections" style={{ marginBottom: 14 }}>
                {tabs.map(([key, label]) => (
                  <button key={key} type="button" role="tab" aria-selected={tab === key}
                    className={'seg-btn' + (tab === key ? ' on' : '')} onClick={() => setTab(key)}>{label}</button>
                ))}
              </div>

              <div role="tabpanel" aria-label={tabs.find(([k]) => k === tab)?.[1]}>
                {tab === 'site' && (
                  <dl className="detail-grid">
                    <dt>Id</dt><dd className="mono">{site.id}</dd>
                    <dt>URL</dt><dd>{site.openUrl ? <a className="link" href={site.openUrl} target="_blank" rel="noreferrer noopener">{short(site.url)}</a> : short(site.url)}</dd>
                    {site.title && <><dt>Title</dt><dd>{site.title}</dd></>}
                    <dt>Created</dt><dd>{fmtDateTime(site.createdAt)}</dd>
                    <dt>Last seen</dt><dd>{site.lastSeenAt ? `${fmtDateTime(site.lastSeenAt)} (${ago(site.lastSeenAt)})` : 'The plugin has never connected'}</dd>
                    <dt>Plugin version</dt><dd className="mono">{site.pluginVersion || '—'}</dd>
                    <dt>Release channel</dt><dd><ReleaseChannel site={site} canChange={canChannel}
                      onChanged={() => { setReload((n) => n + 1); onChanged(); }} /></dd>
                    <dt>WordPress</dt><dd className="mono">{site.wpVersion || '—'}</dd>
                    <dt>Credits used</dt><dd className="mono">{site.creditsUsed.toLocaleString()}</dd>
                    {site.state !== 'active' && <><dt>Deactivated</dt><dd>{site.deactivatedReason || 'unknown reason'}{site.deactivatedAt ? ` · ${fmtDateTime(site.deactivatedAt)}` : ''}</dd></>}
                  </dl>
                )}
                {tab === 'account' && (
                  <dl className="detail-grid">
                    <dt>Email</dt><dd>{site.account.email}</dd>
                    {site.account.name && <><dt>Name</dt><dd>{site.account.name}</dd></>}
                    <dt>Plan</dt><dd>{site.plan.name}</dd>
                    <dt>Account type</dt><dd>{site.account.accountType || '—'} · {site.account.status || '—'}</dd>
                    <dt>Credits</dt><dd className="mono">{site.account.creditsBalance.toLocaleString()} left · {site.account.creditsUsedTotal.toLocaleString()} used in total</dd>
                  </dl>
                )}
                {tab === 'sandbox' && site.sandbox && (
                  <SandboxPanel site={site} canManage={canManage}
                    onChanged={() => { setReload((n) => n + 1); onChanged(); }} />
                )}
                {tab === 'connectors' && (connectors.length === 0 ? <p className="muted small">None connected.</p> : (
                  <div className="row-actions" style={{ flexWrap: 'wrap' }}>
                    {connectors.map((c) => <span key={c} className="badge badge-purple">{c}</span>)}
                  </div>
                ))}
                {tab === 'conversations' && canChats && (
                  site.conversationsRestricted || site.conversations === null
                    ? <p className="muted small">Chat titles need Accounts access.</p>
                    : site.conversations.length === 0 ? <p className="muted small">No conversations on this site yet.</p> : (
                      <div className="conv-grid">
                        {site.conversations.map((c) => (
                          <button key={c.id} type="button" className="conv-card" onClick={() => setChat(c.id)}>
                            <div className="conv-card-head"><span className="conv-card-title">{c.title || 'Untitled'}</span>
                              <span className="conv-card-time">{ago(c.lastMessageAt || c.createdAt)}</span></div>
                            <div className="conv-card-foot"><span className="conv-card-msgs">{c.messageCount} msgs</span></div>
                          </button>
                        ))}
                      </div>
                    )
                )}
                {tab === 'credits' && <CreditHistory site={site} />}
              </div>


              {chat && <ConversationModal conversationId={chat} onClose={() => setChat(null)} />}
            </>
          )}
        </div>
      </div>
    </div>, document.body);
}

const RELEASE_CHANNELS = ['public', 'beta', 'test'] as const;

/**
 * Which plugin builds this site receives (moved here from the Releases page).
 * The plugin picks the change up on its next update check. Only an active
 * site has a plugin asking, so the control is shown for active rows only.
 */
function ReleaseChannel({ site, canChange, onChanged }: { site: SiteDetails; canChange: boolean; onChanged: () => void }) {
  const current = site.releaseChannel || 'public';
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  if (!canChange || site.state !== 'active') return <span className="badge badge-gray">{current}</span>;

  async function change(channel: string) {
    if (channel === current) return;
    setBusy(true); setMsg(null);
    try {
      await backendApi.put(`/license/admin/sites/${encodeURIComponent(site.id)}/channel`, { channel });
      setMsg({ ok: true, text: `Now on the ${channel} channel.` });
      onChanged();
    } catch (err: any) {
      setMsg({ ok: false, text: err.body?.error || err.message });
    } finally { setBusy(false); }
  }

  return (
    <>
      <select aria-label="Release channel" value={current} disabled={busy} onChange={(e) => void change(e.target.value)}>
        {RELEASE_CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      {msg && <span className={'small' + (msg.ok ? ' muted' : '')} style={{ marginLeft: 8, color: msg.ok ? undefined : '#EF4444' }}>{msg.text}</span>}
    </>
  );
}

/** Time left on a sandbox that still expires, e.g. "1h 42m left". */
function timeLeft(expiresAt: string | null): string {
  if (!expiresAt) return '—';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(ms)) return '—';
  if (ms <= 0) return 'expired';
  const h = Math.floor(ms / 3600000); const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m left`;
}

/**
 * Container facts plus the two lifecycle actions the old Trials page had:
 * Extend (+2h) and Keep forever, for a live sandbox that still expires.
 * Both call the existing trial routes (POST /api/trial/:id/extend|persist).
 */
function SandboxPanel({ site, canManage, onChanged }: { site: SiteDetails; canManage: boolean; onChanged: () => void }) {
  const sb = site.sandbox!;
  const [busy, setBusy] = useState<'' | 'extend' | 'keep'>('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function run(kind: 'extend' | 'keep') {
    if (kind === 'keep' && !confirm('Keep this sandbox forever? It will no longer expire.')) return;
    setBusy(kind); setMsg(null);
    try {
      await backendApi.post(`/trial/${encodeURIComponent(sb.trialId!)}/${kind === 'extend' ? 'extend' : 'persist'}`);
      setMsg({ ok: true, text: kind === 'extend' ? 'Extended by 2 hours.' : 'This sandbox is now permanent.' });
      onChanged();
    } catch (err: any) {
      setMsg({ ok: false, text: err.body?.error || err.message });
    } finally { setBusy(''); }
  }

  return (
    <>
      <dl className="detail-grid">
        <dt>Container</dt><dd>{sb.status || 'unknown'}{sb.persisted ? ' (permanent)' : ''}{sb.pool ? ' · unclaimed pool member' : ''}</dd>
        {sb.trialId && <><dt>Trial id</dt><dd className="mono">{sb.trialId}</dd></>}
        {sb.linkName && <><dt>Link name</dt><dd className="mono">{sb.linkName}</dd></>}
        {sb.linkHost && <><dt>Link</dt><dd className="mono">{sb.linkHost}</dd></>}
        {!sb.persisted && sb.expiresAt && <><dt>Expires</dt><dd>{fmtDateTime(sb.expiresAt)} · {timeLeft(sb.expiresAt)}</dd></>}
      </dl>
      {msg && <div className={'alert ' + (msg.ok ? 'alert-ok' : 'alert-error')} style={{ marginTop: 10 }}>{msg.text}</div>}
      {canManage && canExtendOrKeep(sb) && (
        <div className="row-actions" style={{ marginTop: 10 }}>
          <button className="btn btn-sm" disabled={!!busy} onClick={() => void run('extend')}>{busy === 'extend' ? 'Extending…' : 'Extend +2h'}</button>
          <button className="btn btn-sm btn-primary" disabled={!!busy} onClick={() => void run('keep')}>{busy === 'keep' ? 'Saving…' : 'Keep forever'}</button>
        </div>
      )}
    </>
  );
}

/**
 * The site's credit history, ten rows at a time from the paged route. A
 * backend without that route (404) falls back to the last ten rows the
 * details payload already carries, without paging.
 */
function CreditHistory({ site }: { site: SiteDetails }) {
  const [page, setPage] = useState(0);
  const [data, setData] = useState<ActivityPage | null>(null);
  const [fallback, setFallback] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    backendApi.get<ActivityPage>(activityPath(site.id, page))
      .then((d) => { if (live) { setData(d); setError(''); } })
      .catch((err: any) => {
        if (!live) return;
        if (isEndpointMissing(err)) setFallback(true);
        else setError(err.message);
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [site.id, page]);

  const rows: ActivityRow[] = fallback ? site.recentActivity : (data?.activity ?? []);
  const total = fallback ? rows.length : (data?.total ?? 0);
  const lastPage = Math.max(0, Math.ceil(total / ACTIVITY_PAGE_SIZE) - 1);
  const from = total === 0 ? 0 : page * ACTIVITY_PAGE_SIZE + 1;
  const to = Math.min(total, page * ACTIVITY_PAGE_SIZE + rows.length);

  return (
    <>
      <h4 className="detail-h">Credit usage{!fallback && data ? ` (${total.toLocaleString()})` : ''}</h4>
      {error ? <div className="alert alert-error">{error}</div>
        : loading && !data && !fallback ? <p className="muted small">Loading…</p>
        : rows.length === 0 ? <p className="muted small">No credit activity for this site.</p> : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>When</th><th>Type</th><th>Credits</th><th>Description</th></tr></thead>
                <tbody>
                  {rows.map((a, i) => (
                    <tr key={`${page}-${i}`}>
                      <td className="muted">{fmtDateTime(a.at)}</td><td>{a.type}</td>
                      <td className="mono">{a.credits > 0 ? `+${a.credits}` : a.credits}</td>
                      <td className="muted">{a.description || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!fallback && (
              <div className="filter-bar" style={{ justifyContent: 'space-between', marginTop: 8 }}>
                <span className="muted small" aria-live="polite">Showing {from}–{to} of {total.toLocaleString()}</span>
                <div className="row-actions">
                  <button className="btn btn-sm" aria-label="Previous credit page" disabled={loading || page <= 0}
                    onClick={() => setPage((p) => p - 1)}>Previous</button>
                  <span className="muted small">Page {page + 1} of {lastPage + 1}</span>
                  <button className="btn btn-sm" aria-label="Next credit page" disabled={loading || page >= lastPage}
                    onClick={() => setPage((p) => p + 1)}>Next</button>
                </div>
              </div>
            )}
          </>
        )}
    </>
  );
}
